import React, { useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import CheckFormWrapper from '../shared/CheckFormWrapper';
import FileUploadField from '../shared/FileUploadField';
import TagsInput from '../shared/TagsInput';

/**
 * TerminationCheckForm
 *
 * Checks whether terminated users were deactivated before or after
 * their last working day and calculates the delay.
 *
 * API: POST /helper/termination-check
 *
 * Props:
 *   onSubmit   - (FormData) => void
 *   isLoading  - bool
 */
export default function TerminationCheckForm({ onSubmit, isLoading = false }) {
  const [inputFile, setInputFile] = useState(null);
  const [terminationFile, setTerminationFile] = useState(null);

  // userNameCol – [srcCol] or [srcCol, mergeCol]; order = source first, optional merge second
  const [userNameCol, setUserNameCol] = useState([]);
  const [userNameColError, setUserNameColError] = useState('');

  const [lastValidityCol, setLastValidityCol] = useState('');
  const [lastWorkingDayCol, setLastWorkingDayCol] = useState('');
  const [lastLogonDayCol, setLastLogonDayCol] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();

    if (userNameCol.length === 0) {
      setUserNameColError('Add at least one column name');
      return;
    }
    setUserNameColError('');

    const formData = new FormData();
    formData.append('inputFile', inputFile);
    formData.append('terminationFile', terminationFile);
    formData.append('userNameCol', JSON.stringify(userNameCol));
    formData.append('lastValidityCol', lastValidityCol);
    formData.append('lastWorkingDayCol', lastWorkingDayCol);
    formData.append('lastLogonDayCol', lastLogonDayCol);

    onSubmit(formData);
  };

  return (
    <CheckFormWrapper
      title="Termination Check"
      description="Verifies whether terminated users' accounts were deactivated on time relative to their last working day."
      onSubmit={handleSubmit}
      isLoading={isLoading}
    >
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <FileUploadField
          label="User Account Extract"
          file={inputFile}
          onChange={setInputFile}
          required
          helperText=".csv or .xlsx"
        />
        <FileUploadField
          label="Termination List"
          file={terminationFile}
          onChange={setTerminationFile}
          required
          helperText=".xlsx – list of terminated employees"
        />
      </Box>

      <Box>
        <TagsInput
          label={
            <>
              User Name Column <span style={{ color: '#d32f2f' }}>*</span>
            </>
          }
          values={userNameCol}
          onChange={(next) => {
            setUserNameCol(next);
            if (next.length > 0) setUserNameColError('');
          }}
          placeholder="Column name (e.g. UserID), then Enter or Add"
          helperText="First item = source column in the extract. Add a second item only if you use a separate merge column."
        />
        {userNameColError && (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: 'block', mt: 0.5 }}
          >
            {userNameColError}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Last Validity Column"
          value={lastValidityCol}
          onChange={(e) => setLastValidityCol(e.target.value)}
          required
          sx={{ minWidth: 220, maxWidth: 280 }}
          helperText="Column containing the account validity/expiry date"
        />
        <TextField
          size="small"
          label="Last Working Day Column"
          value={lastWorkingDayCol}
          onChange={(e) => setLastWorkingDayCol(e.target.value)}
          required
          sx={{ minWidth: 220, maxWidth: 280 }}
          helperText="Column from the termination file"
        />
        <TextField
          size="small"
          label="Last Logon Day Column"
          value={lastLogonDayCol}
          onChange={(e) => setLastLogonDayCol(e.target.value)}
          required
          sx={{ minWidth: 220, maxWidth: 280 }}
          helperText="Column containing the most recent login date"
        />
      </Box>
    </CheckFormWrapper>
  );
}
